/**
 * QuestionCardV2 - Main question display card
 *
 * Features:
 * - Modern card design with subtle gradients
 * - Question metadata badges
 * - Question content with math support
 * - Answer section (MCQ or SPR)
 * - Question controls (confidence, mark for review)
 */

import { memo } from 'react'
import {
  useChallengeV2 as useChallengeStoreV2,
  selectCurrentQuestion,
  selectIsMCQ,
  selectIsLoading,
  selectProgress,
  selectFormattedQuestionTime
} from '../../../hooks/useChallenge'

import QuestionContentV2 from './QuestionContentV2'
import QuestionMetaBadgesV2 from './QuestionMetaBadgesV2'
import MultipleChoiceV2 from '../answer/MultipleChoiceV2'
import StudentResponseV2 from '../answer/StudentResponseV2'
import QuestionControlsV2 from '../controls/QuestionControlsV2'

const QuestionCardV2 = memo(() => {
  const currentQuestion = useChallengeStoreV2(selectCurrentQuestion)
  const isMCQ = useChallengeStoreV2(selectIsMCQ)
  const isLoading = useChallengeStoreV2(selectIsLoading)
  const currentQuestionIndex = useChallengeStoreV2((state) => state.currentQuestionIndex)
  const progress = useChallengeStoreV2(selectProgress)
  const questionTime = useChallengeStoreV2(selectFormattedQuestionTime)
  const isPaused = useChallengeStoreV2((state) => state.isPaused)

  // Loading state
  if (isLoading || !currentQuestion) {
    return (
      <div className="animate-pulse">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 sm:p-8">
          <div className="flex gap-2 mb-4">
            <div className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
            <div className="h-6 w-24 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
            <div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          </div>
          <div className="mt-8 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-14 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Question Card */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden">
        {/* Top accent gradient */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

        <div
          className={`p-4 sm:p-6 lg:p-8 transition-all duration-300 ${isPaused ? 'blur-lg select-none pointer-events-none' : ''}`}
        >
          {/* Header: Question number, timer, and metadata */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-bold text-sm shadow-md">
                {currentQuestionIndex + 1}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">of {progress.total}</span>
              {/* Question timer */}
              <span
                className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-mono font-medium"
                title="Time spent on this question"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="tabular-nums">{questionTime}</span>
              </span>
            </div>
            <QuestionMetaBadgesV2 question={currentQuestion} />
          </div>

          {/* Question Content */}
          <QuestionContentV2 question={currentQuestion} />

          {/* Divider */}
          <div className="my-6 border-t border-gray-200 dark:border-gray-700" />

          {/* Answer Section */}
          {isMCQ ? <MultipleChoiceV2 question={currentQuestion} /> : <StudentResponseV2 />}
        </div>
      </div>

      {/* Controls Card */}
      <QuestionControlsV2 />
    </div>
  )
})

QuestionCardV2.displayName = 'QuestionCardV2'

export default QuestionCardV2
